/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, {useEffect, useState} from "react";
import ToolBarBox from "./ToolBarBox";
import { useLanguage } from "../../../LanguageContext";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmAnalysesDialog from "../Analyses/FormComponents/ConfirmAnalysesDialog";
import { createAnalysis } from "../../../services/analysisService";
import { useNavBarLabelsService } from "services/useNavBarLabelsService";
import Loader from "../Loader/Loader";
import { useIsMobile } from "hooks/IsMobile";
import { Icon } from "@coreai/component-library";

let toCamelCase = function (str: string): string {
  return str
    .replace(/(^|\s)([a-zA-Z])/g, (match, p1, p2, offset) =>
      offset === 0 ? p2.toLowerCase() : ' ' + p2.toUpperCase()
    )
    .replace(/\s+/g, ' ').trim();
};

declare global {
  interface Window {
    reRenderAnalysisList: boolean;
    _paq: any[];
  }
}

export default function ToolBar() {
  const { t, setReRenderAnalysisList } = useLanguage();
  const [confirmBox, setConfirmBox] = useState<boolean>(false);    
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { navbarLabels, loading } = useNavBarLabelsService();
  const isMobile = useIsMobile();

  let pathnames: any = {
    "/projects": "Projects",
    "/analyses": "Analyses",
    "/rulesets": "Reference Frames",
    "/help": "FAQ",
    "/about": "About",
    "/contact": "Contact"
  };

  useEffect(()=>{
    setConfirmBox(localStorage.getItem("confirmBox")==='open');
  },[])

  useEffect(() => {
    const _paq = (window._paq = window._paq || []);
    _paq.push(['setCustomUrl', location.pathname]);
    let documentTitle = location.pathname.toLowerCase();
    
    if (documentTitle === "") documentTitle = "Analysis";
    else if (pathnames?.[documentTitle]) documentTitle = pathnames[documentTitle];
    else documentTitle = toCamelCase(documentTitle.split('/').join('  ')).trim();

    _paq.push(['setDocumentTitle', documentTitle]);
    _paq.push(['trackPageView']);
  }, [location]);

  const closeConfirm = () => {
    setConfirmBox(false);
    localStorage.setItem("confirmBox","close");
    localStorage.removeItem("analysis");
  };

  const launchAnalysis = () => {
    const analysis = localStorage.getItem("analysis");
    const parsedAnalysis: any = analysis ? JSON.parse(analysis) : null;
    parsedAnalysis.status = "draft";
    createAnalysis(parsedAnalysis)
      .then(() => {
        localStorage.removeItem("analysis");
        localStorage.setItem("confirmBox","close");
        setReRenderAnalysisList(true);
      })
      .catch(console.error);
  };
  const okConfirm = () => launchAnalysis();

  const contactWrapper = css`
    display: block;
  `;

  const twinToolWrapper = css`
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 6px;
    justify-content: center;
    padding: 8px 12px 16px 12px;
    cursor: pointer;
  `;

  const twinToolIcon = css`
    width: 32px;
    height: 32px;
  `;

  const twinToolTitle = css`
    margin-top: 0px;
    font-size: 10px;
    font-weight: 600;
    text-align: center;
    color: #081023;
    display: none;
  `;

  const exploreStyle = css`
    font-size: 10px;
    font-weight: 600;
    color: #ACB8BD;
    padding: 10px 0;
    border-top: 1px solid #EAEEF0;
    text-align: center;
  `;

  const toolbarStyles = css`
    z-index: 1000;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100px;
    height: calc(100vh - 90px);
    border-right: 1px solid #ccc;
    position: fixed;
    top: 90px;
    background-color: #fff;

    @media (max-width: 1280px){
      width: 5rem;
    }

    @media (max-width: 768px) {
      // position: fixed;
      flex-direction: row;
      width: 100%;
      height: 84px;
      border-right: none;
      border-top: none;
      top: auto;
      bottom: 0;
      left: 0;
      right: 0;

      .itemWrapper{
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: flex-start;
        overflow: hidden;
        box-shadow: 4px 4px 30px 0px #00000026;

      }
    }
  `;

  const moreMenuPopup = css`
    position: fixed;
    bottom: 84px;
    right: 0px;
    background: white;
    border-radius: 12px 0 0 0;
    box-shadow: 4px 4px 30px 0px #00000026;
    z-index: 1000;
    overflow: hidden;
    min-width: 20%;
  `;

  const toolbarItems = [
    {
      title: t("toolbar_projects"),
      iconName: "folder",
      isActive: false,
      path: "/projects",
    },
    {
      title: t("toolbar_analysis"),
      iconName: "dashboard",
      isActive: false,
      path: "/analyses",
    },
    {
      title: t("toolbar_referenceFrame"),
      iconName: "book-open",
      isActive: false,
      path: "/rulesets",
    },
    {
      title: t("toolbar_help"),
      iconName: "help",
      isActive: false,
      path: "/help",
    },
    {
      title: t("toolbar_about"),
      iconName: "info-circle",
      isActive: false,
      path: "/about",
    },
    {
      title: t("toolbar_contact"),
      iconName: "mail",
      isActive: false,
      path: "/contact",
    },
  ];

  const hideFromBottomOnly = ["/about"];
  const hideCompletely = ["/contact"];


  let navItems = toolbarItems.filter(
    (item) => !(isMobile && [...hideFromBottomOnly, ...hideCompletely].includes(item.path))
  );


  if (isMobile) {
    navItems.push({
      title: "More",
      iconName: "menu",
      isActive: false,
      path: "/more",
    });
  }

  const exploreEle = () => {
    return (
      <div css={contactWrapper}>
        <div css={exploreStyle}>{t("toolbar_explore")}</div>
  
        {loading ? (
          <Loader />
        ) : (
          <div
            css={twinToolWrapper}
            onClick={() => {
              const currentHost = window.location.origin;
              const target = currentHost.includes("antigreenwashing")
                ? currentHost.replace("antigreenwashing", "positiverepresentations")
                : currentHost.replace("positiverepresentations", "antigreenwashing");
  
              window.open(target + "/signin", "_blank");
            }}
          >
            <img
              src={navbarLabels?.twinTool_logo}
              alt="Logo"
              css={twinToolIcon}
            />
            <p css={twinToolTitle}>{navbarLabels?.twinTool_title}</p>
          </div>
        )}
      </div>
    );
  };  

  return (
    <div className="toolbarContainer" css={toolbarStyles}>
      <div className="itemWrapper" role="navigation">
        {navItems.map((item) => (
          <ToolBarBox
            key={item.path}
            title={item.title}
            iconName={item.iconName}
            isActive={
              isMobile && showMoreMenu && item.path === "/more"
                ? true
                : !showMoreMenu &&
                  (location.pathname.startsWith(item.path) ||
                    (location.pathname === "/" && item.path === "/analyses"))
            }
            path={item.path}
            onClick={() => {
              if (item.path === "/more") {
                setShowMoreMenu(prev => !prev);
              } else {
                setShowMoreMenu(false);
                navigate(item.path);
              }
            }}          
          />
        ))}
      </div>

      {isMobile && showMoreMenu && (
        <div css={moreMenuPopup}>
          {hideFromBottomOnly.map(path => {
            const item = toolbarItems.find(i => i.path === path);
            if (!item) return null;
            return (
              <ToolBarBox
                key={item.path}
                title={item.title}
                iconName={item.iconName}
                isActive={location.pathname.startsWith(item.path) || (location.pathname === "/" && item.path=== "/analyses")}
                path={item.path}
                onClick={() => item.path === "/more" && setShowMoreMenu(p => !p)}
              />
            );
          })}
          {exploreEle()}
        </div>
      )}
      {!isMobile && exploreEle()}
      <ConfirmAnalysesDialog analysis={[]} open={confirmBox} onClose={closeConfirm} onOk={okConfirm} />
    </div>
  );
}
